import { visitedDates } from './visits.js';

// Pure aggregate stats over the restaurant list (lifted from App.jsx useMemos, semantics unchanged).

// Average of rated, non-deleted places: 0 when none, otherwise a one-decimal string (as displayed)
export function averageRating(restaurants) {
    const rated = restaurants.filter(r => typeof r.rating === 'number' && !r.deletedAt);
    if (rated.length === 0) return 0;
    return (rated.reduce((sum, r) => sum + r.rating, 0) / rated.length).toFixed(1);
}

export function ratedCount(restaurants) {
    return restaurants.filter(r => typeof r.rating === 'number' && !r.deletedAt).length;
}

export function activeRestaurantCount(restaurants) {
    return restaurants.filter(r => !r.deletedAt).length;
}

// Check-game battle stats, money stats, best/worst guesses, most visited, top rated, rating distribution, all checks
export function computeStats(restaurants) {
    let dougWins = 0;
    let lesWins = 0;
    let ties = 0;
    let totalChecks = 0;
    let dougAvgDiff = 0;
    let lesAvgDiff = 0;
    let totalSpent = 0;
    let dougBestGuess = { diff: Infinity, amount: 0 };
    let lesBestGuess = { diff: Infinity, amount: 0 };
    let dougWorstGuess = { diff: 0, amount: 0 };
    let lesWorstGuess = { diff: 0, amount: 0 };
    let highestCheck = { amount: 0, restaurant: '' };
    let lowestCheck = { amount: Infinity, restaurant: '' };

    const restaurantCheckCounts = {};
    const allChecks = [];

    restaurants.forEach(r => {
        if (r.checks && r.checks.length > 0) {
            restaurantCheckCounts[r.name] = r.checks.length;

            r.checks.forEach(check => {
                totalChecks++;
                totalSpent += check.actualAmount;
                allChecks.push({ ...check, restaurantName: r.name });

                if (check.winner === 'doug') dougWins++;
                if (check.winner === 'les') lesWins++;
                if (check.winner === 'tie') ties++;

                dougAvgDiff += check.dougDiff;
                lesAvgDiff += check.lesDiff;

                // Best guesses
                if (check.dougDiff < dougBestGuess.diff) {
                    dougBestGuess = { diff: check.dougDiff, amount: check.actualAmount, restaurant: r.name };
                }
                if (check.lesDiff < lesBestGuess.diff) {
                    lesBestGuess = { diff: check.lesDiff, amount: check.actualAmount, restaurant: r.name };
                }

                // Worst guesses
                if (check.dougDiff > dougWorstGuess.diff) {
                    dougWorstGuess = { diff: check.dougDiff, amount: check.actualAmount, restaurant: r.name };
                }
                if (check.lesDiff > lesWorstGuess.diff) {
                    lesWorstGuess = { diff: check.lesDiff, amount: check.actualAmount, restaurant: r.name };
                }

                // High/Low checks
                if (check.actualAmount > highestCheck.amount) {
                    highestCheck = { amount: check.actualAmount, restaurant: r.name };
                }
                if (check.actualAmount < lowestCheck.amount) {
                    lowestCheck = { amount: check.actualAmount, restaurant: r.name };
                }
            });
        }
    });

    // Most visited restaurant
    // Most visited = most distinct nights out (visits or check games), live places only
    let mostVisited = { name: '', count: 0 };
    restaurants.filter(r => !r.deletedAt).forEach(r => {
        const count = visitedDates(r).length;
        if (count > mostVisited.count) mostVisited = { name: r.name, count };
    });

    const ratingDistribution = { 10: 0, 9: 0, 8: 0, 7: 0, 6: 0, 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    const topRated = [];

    restaurants.forEach(r => {
        if (typeof r.rating === 'number' && !r.deletedAt) {
            ratingDistribution[Math.min(10, Math.max(1, Math.round(r.rating)))]++;
            topRated.push({ name: r.name, rating: r.rating });
        }
    });

    topRated.sort((a, b) => b.rating - a.rating);

    return {
        dougWins,
        lesWins,
        ties,
        totalChecks,
        dougAvgDiff: totalChecks > 0 ? (dougAvgDiff / totalChecks).toFixed(2) : 0,
        lesAvgDiff: totalChecks > 0 ? (lesAvgDiff / totalChecks).toFixed(2) : 0,
        totalSpent,
        avgCheck: totalChecks > 0 ? (totalSpent / totalChecks).toFixed(2) : 0,
        highestCheck,
        lowestCheck,
        dougBestGuess,
        lesBestGuess,
        dougWorstGuess,
        lesWorstGuess,
        mostVisited,
        topRated: topRated.slice(0, 5),
        ratingDistribution,
        allChecks: allChecks.sort((a, b) => new Date(b.date) - new Date(a.date))
    };
}

// ---------------------------------------------------------------- "Our Year in Restaurants"
import { isoToLocalDate as _isoToLocalDate } from './date.js';
import { averageOfVisits as _avg, visitPairs as _pairs } from './visits.js';

const inYear = (dateStr, year) => year === 'all' || (typeof dateStr === 'string' && dateStr.slice(0, 4) === String(year));

/**
 * Recap for one calendar year (local dates) or 'all'. Pure; trashed places excluded.
 * { year, placesTried, nightsOut, newPlaces, totalSpent, checks, checkBattle{doug,les,ties,winner},
 *   favoritePlace{name,rating,nights}, bestDateNight{name,date,doug,les,sum}, topCuisine{id,count}, closestGuess{who,diff,name} }
 */
export function yearRecap(restaurants, year) {
    const live = (restaurants || []).filter(r => !r.deletedAt);
    let nightsOut = 0, totalSpent = 0, checks = 0, dougWins = 0, lesWins = 0, ties = 0, newPlaces = 0;
    const tried = [];
    let bestDateNight = null;
    let closestGuess = null;
    const cuisineNights = {};

    for (const r of live) {
        if (inYear(_isoToLocalDate(r.dateAdded), year)) newPlaces++;
        const nights = new Set();
        for (const v of r.visits || []) if (inYear(v.date, year)) nights.add(v.date);
        for (const c of r.checks || []) {
            const d = _isoToLocalDate(c.date);
            if (!inYear(d, year)) continue;
            nights.add(d);
            checks++;
            totalSpent += Number(c.actualAmount) || 0;
            if (c.winner === 'doug') dougWins++; else if (c.winner === 'les') lesWins++; else ties++;
            for (const who of ['doug', 'les']) {
                const diff = Number(c[`${who}Diff`]);
                if (Number.isFinite(diff) && (!closestGuess || diff < closestGuess.diff)) closestGuess = { who, diff, name: r.name };
            }
        }
        if (nights.size) {
            nightsOut += nights.size;
            const visitsThisYear = (r.visits || []).filter(v => inYear(v.date, year));
            tried.push({ name: r.name, nights: nights.size, rating: _avg(visitsThisYear) ?? (typeof r.rating === 'number' ? r.rating : null) });
            for (const c of r.cuisine || []) cuisineNights[c] = (cuisineNights[c] || 0) + nights.size;
        }
        for (const p of _pairs(r.visits)) {
            if (!inYear(p.date, year) || !p.doug || !p.les) continue;
            const sum = p.doug.rating + p.les.rating;
            if (!bestDateNight || sum > bestDateNight.sum || (sum === bestDateNight.sum && p.date > bestDateNight.date)) {
                bestDateNight = { name: r.name, date: p.date, doug: p.doug.rating, les: p.les.rating, sum };
            }
        }
    }

    const favoritePlace = tried
        .filter(x => typeof x.rating === 'number')
        .sort((a, b) => b.rating - a.rating || b.nights - a.nights || a.name.localeCompare(b.name))[0] || null;
    const topCuisineId = Object.keys(cuisineNights).sort((a, b) => cuisineNights[b] - cuisineNights[a] || a.localeCompare(b))[0];

    return {
        year,
        placesTried: tried.length,
        nightsOut,
        newPlaces,
        totalSpent: Math.round(totalSpent * 100) / 100,
        checks,
        checkBattle: { doug: dougWins, les: lesWins, ties, winner: dougWins > lesWins ? 'doug' : lesWins > dougWins ? 'les' : checks ? 'tie' : null },
        favoritePlace,
        bestDateNight,
        topCuisine: topCuisineId ? { id: topCuisineId, count: cuisineNights[topCuisineId] } : null,
        closestGuess,
    };
}

/** Years that have any activity, newest first. */
export function recapYears(restaurants) {
    const years = new Set();
    for (const r of restaurants || []) {
        if (r.deletedAt) continue;
        for (const v of r.visits || []) if (v.date) years.add(v.date.slice(0, 4));
        for (const c of r.checks || []) { const d = _isoToLocalDate(c.date); if (d) years.add(d.slice(0, 4)); }
    }
    return [...years].sort().reverse();
}
